import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Card, 
  CardContent,
  Grid,
  Divider,
  Paper,
  Snackbar,
  Alert
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import html2pdf from 'html2pdf.js';

const PenalitatiGenerale = () => {
  const [valoarePrincipala, setValoarePrincipala] = useState('');
  const [procentPenalitate, setProcentPenalitate] = useState('');
  const [numarZile, setNumarZile] = useState('');
  const [rezultat, setRezultat] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // Calculează automat când se schimbă valorile
  useEffect(() => {
    if (valoarePrincipala && procentPenalitate && numarZile) {
      const valoare = parseFloat(valoarePrincipala);
      const procent = parseFloat(procentPenalitate);
      const zile = parseInt(numarZile);
      
      if (valoare > 0 && procent >= 0 && zile > 0) {
        const penalitateTotal = (valoare * procent / 100) * zile;
        const totalDePlata = valoare + penalitateTotal;
        
        setRezultat({
          valoarePrincipala: valoare.toFixed(2),
          procentPenalitate: procent.toFixed(2),
          numarZile: zile,
          penalitateTotal: penalitateTotal.toFixed(2),
          totalDePlata: totalDePlata.toFixed(2),
          calculDesfasurat: `Penalități = Suma × (Procent/100) × Nr. zile întârziere = ${valoare.toFixed(2)} × ${procent}% × ${zile} ${zile === 1 ? 'zi' : 'zile'} = ${penalitateTotal.toFixed(2)} lei`
        });
      }
    } else {
      setRezultat(null);
    }
  }, [valoarePrincipala, procentPenalitate, numarZile]);

  const resetCalculator = () => {
    setValoarePrincipala('');
    setProcentPenalitate('');
    setNumarZile('');
    setRezultat(null);
  };

  const copiazaRezultat = () => {
    if (rezultat) {
      const textDeCopiat = `Pentru ${rezultat.numarZile} ${rezultat.numarZile === 1 ? 'zi' : 'zile'} întârziere, suma de plată este ${rezultat.totalDePlata} lei, din care ${rezultat.penalitateTotal} lei penalități.`;
      navigator.clipboard.writeText(textDeCopiat).then(() => {
        setOpenSnackbar(true);
      });
    }
  };

  const exportaPDF = () => {
    if (rezultat) {
      // Creează un element HTML pentru PDF
      const element = document.createElement('div');
      element.innerHTML = `
        <div style="padding: 20px; font-family: Arial, sans-serif;">
          <h2 style="text-align: center; color: #333; border-bottom: 2px solid #ccc; padding-bottom: 10px;">
            Rezultatul Calculului Penalități
          </h2>
          
          <div style="margin: 20px 0;">
            <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
              <span><strong>Numărul de zile de întârziere:</strong></span>
              <span><strong>${rezultat.numarZile} ${rezultat.numarZile === 1 ? 'zi' : 'zile'}</strong></span>
            </div>
            
            <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
              <span><strong>Valoarea totală a penalităților:</strong></span>
              <span style="color: #dc3545;"><strong>${rezultat.penalitateTotal} lei</strong></span>
            </div>
            
            <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 2px solid #ccc;">
              <span><strong>Total de plată:</strong></span>
              <span style="color: #28a745; font-size: 18px;"><strong>${rezultat.totalDePlata} lei</strong></span>
            </div>
          </div>
          
          <div style="margin-top: 20px; padding: 15px; background-color: #f8f9fa; border: 1px solid #dee2e6; border-radius: 5px;">
            <h4 style="margin-bottom: 10px;">Calcul desfășurat:</h4>
            <p style="font-style: italic; margin: 0;">${rezultat.calculDesfasurat}</p>
          </div>
        </div>
      `;

      const opt = {
        margin: 1,
        filename: 'calcul-penalitati.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };

      html2pdf().set(opt).from(element).save();
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        <CalculateIcon style={{ marginRight: '8px', verticalAlign: 'middle' }} />
        Calculator Penalități Generale
      </Typography>
      
      <Card sx={{ marginTop: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Valoarea principală (RON)"
                type="number"
                value={valoarePrincipala}
                onChange={(e) => setValoarePrincipala(e.target.value)}
                variant="outlined"
                size="small"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Procentul penalității (%)"
                type="number"
                value={procentPenalitate}
                onChange={(e) => setProcentPenalitate(e.target.value)}
                variant="outlined"
                size="small"
                inputProps={{ step: "0.01" }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Numărul de zile de întârziere"
                type="number"
                value={numarZile}
                onChange={(e) => setNumarZile(e.target.value)}
                variant="outlined"
                size="small"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Button
                size="small"
                fullWidth
                variant="outlined"
                onClick={resetCalculator}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {rezultat && (
        <Paper 
          elevation={3} 
          sx={{ 
            marginTop: 3, 
            padding: 3, 
            backgroundColor: '#f8f9fa',
            border: '2px solid #e9ecef',
            borderRadius: 2
          }}
        >
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              textAlign: 'center', 
              fontWeight: 'bold',
              color: '#495057',
              marginBottom: 3
            }}
          >
            Rezultatul Calculului
          </Typography>
          
          <Box sx={{ marginBottom: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                Numărul de zile de întârziere:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {rezultat.numarZile} {rezultat.numarZile === 1 ? 'zi' : 'zile'}
              </Typography>
            </Box>
            <Divider sx={{ marginY: 1 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                Valoarea totală a penalităților:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#dc3545' }}>
                {rezultat.penalitateTotal} lei
              </Typography>
            </Box>
            <Divider sx={{ marginY: 1 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                Total de plată:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#28a745' }}>
                {rezultat.totalDePlata} lei
              </Typography>
            </Box>
            <Divider sx={{ marginY: 2 }} />
            
            <Box sx={{ marginTop: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                Calcul desfășurat:
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontStyle: 'italic',
                  backgroundColor: '#ffffff',
                  padding: 2,
                  borderRadius: 1,
                  border: '1px solid #dee2e6'
                }}
              >
                {rezultat.calculDesfasurat}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', marginTop: 3 }}>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<PictureAsPdfIcon />}
              onClick={exportaPDF}
            >
              Export PDF
            </Button>
            
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<ContentCopyIcon />}
              onClick={copiazaRezultat}
            >
              Copiază rezultatul
            </Button>
          </Box>
        </Paper>
      )}
      
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success">
          Rezultatul a fost copiat în clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PenalitatiGenerale;